import { KnotBlockerModel } from 'game/boardMVC/models/blockers/KnotBlockerModel';
import { RocketBlockerModel } from 'game/boardMVC/models/blockers/RocketBlockerModel';
import { MeasureTapeBlockerModel } from 'game/boardMVC/models/blockers/MeasureTapeBlockerModel';
import cloneDeep from 'lodash/cloneDeep';
import { Signal } from 'typed-signals';
import type { LevelData } from '../level/LevelData';
import type { LevelTaskProgress, LevelTaskTallies } from './types';
import { dioramaFillConversion, LevelTaskType } from './types';
import MathUtils from '../../utils/MathUtils';
import {
  convertTasksToProgress,
  getCombinedTasksGoals,
  getCombinedTasksTally,
  mergeTaskTalliesWithProgress,
} from './LevelTasksUtils';
import { TileModel } from '../boardMVC/models/board/TileModel';
import { PlankBlockerModel } from '../boardMVC/models/blockers/PlankBlockerModel';
import { TapeBlockerModel } from '../boardMVC/models/blockers/TapeBlockerModel';
import { BirdHouseBlockerModel } from '../boardMVC/models/blockers/BirdHouseBlockerModel';
import { StarBlockerModel } from '../boardMVC/models/blockers/StarBlockerModel';
import { TileColor } from '../boardMVC/TileColor';

export type LevelTaskItem =
  | TileModel
  | PlankBlockerModel
  | TapeBlockerModel
  | KnotBlockerModel
  | BirdHouseBlockerModel
  | RocketBlockerModel
  | MeasureTapeBlockerModel
  | StarBlockerModel;

const levelTaskItemChecker: Record<LevelTaskType, (item: object) => boolean> = {
  [LevelTaskType.Hexes]: (item) => item instanceof TileModel,
  [LevelTaskType.TapeBlockers]: (item) => item instanceof TapeBlockerModel,
  [LevelTaskType.PlankBlockers]: (item) => item instanceof PlankBlockerModel && item.getColor() === TileColor.None,
  [LevelTaskType.Birds]: (item) => item instanceof BirdHouseBlockerModel,
  [LevelTaskType.KnotBlockers]: (item) => item instanceof KnotBlockerModel,
  [LevelTaskType.Rocket]: (item) => item instanceof RocketBlockerModel,
  [LevelTaskType.Stars]: (item) => item instanceof StarBlockerModel,
  [LevelTaskType.MeasureTape]: (item) => item instanceof MeasureTapeBlockerModel,
  [LevelTaskType.PlankRed]: (item) => item instanceof PlankBlockerModel && item.getColor() === TileColor.Red,
  [LevelTaskType.PlankGreen]: (item) => item instanceof PlankBlockerModel && item.getColor() === TileColor.Green,
  [LevelTaskType.PlankBlue]: (item) => item instanceof PlankBlockerModel && item.getColor() === TileColor.Blue,
  [LevelTaskType.PlankPink]: (item) => item instanceof PlankBlockerModel && item.getColor() === TileColor.Pink,
  [LevelTaskType.PlankYellow]: (item) => item instanceof PlankBlockerModel && item.getColor() === TileColor.Yellow,
  [LevelTaskType.PlankGrey]: (item) => item instanceof PlankBlockerModel && item.getColor() === TileColor.Grey,
  [LevelTaskType.PlankWhite]: (item) => item instanceof PlankBlockerModel && item.getColor() === TileColor.White,
  [LevelTaskType.PlankNavy]: (item) => item instanceof PlankBlockerModel && item.getColor() === TileColor.Navy,
  [LevelTaskType.PlankPurple]: (item) => item instanceof PlankBlockerModel && item.getColor() === TileColor.Purple,
  [LevelTaskType.PlankAny]: (item) => item instanceof PlankBlockerModel,
};

/**
 * Class for tracking / updating progress for LevelTasks.
 */
export class LevelTasksTracker {
  public readonly onProgressUpdated = new Signal<(combinedProgress: number) => void>();

  public readonly tasks: LevelTaskProgress[];

  private combinedTally = 0;

  public readonly combinedGoals: number;

  constructor(levelData: LevelData, taskTallies: LevelTaskTallies) {
    this.tasks = mergeTaskTalliesWithProgress(taskTallies, convertTasksToProgress(levelData));
    this.combinedGoals = getCombinedTasksGoals(this.tasks);
    this.combinedTally = getCombinedTasksTally(this.tasks);
  }

  public setTaskItemValue(taskType: LevelTaskType, value: number): void {
    const task = this.tasks.find(({ type }) => type === taskType);
    if (!task) return;

    task.collected = value;

    this.combinedTally = getCombinedTasksTally(this.tasks);

    this.onProgressUpdated.emit(this.getCombinedProgress());
  }

  public trackTaskItem(item: LevelTaskItem): LevelTaskProgress | undefined {
    const taskType = this.getItemTaskType(item);
    if (!taskType) return undefined;

    const task = this.tasks.find(({ type }) => type === taskType);
    if (!task) return undefined;

    task.collected += 1;

    this.combinedTally = getCombinedTasksTally(this.tasks);

    this.onProgressUpdated.emit(this.getCombinedProgress());

    return task;
  }

  public getCombinedProgress(): number {
    return MathUtils.clamp(this.combinedTally / this.combinedGoals, 0, 1);
  }

  public getTaskProgress(taskType: LevelTaskType): LevelTaskProgress | undefined {
    return this.tasks.find(({ type }) => type === taskType);
  }

  public get allTasksComplete(): boolean {
    return this.combinedTally >= this.combinedGoals;
  }

  public getCombinedTally(): number {
    return this.combinedTally;
  }

  public getTaskTally(taskType: LevelTaskType): number {
    return this.getTaskProgress(taskType)?.collected ?? 0;
  }

  public getTaskTallies(): LevelTaskTallies {
    const tallies: LevelTaskTallies = {};
    for (let i = 0; i < this.tasks.length; i += 1) {
      const task = this.tasks[i];
      tallies[task.type] = task.collected;
    }
    return tallies;
  }

  public getAllTaskProgress(): LevelTaskProgress[] {
    return cloneDeep(this.tasks);
  }

  public getHexTally(): number {
    return this.getTaskTally(LevelTaskType.Hexes);
  }

  public getDioramaFillAmount(): number {
    const taskTypes = this.tasks.map(({ type }) => type);

    if (taskTypes.includes(LevelTaskType.Hexes)) {
      return this.getHexTally();
    }

    return taskTypes.reduce((acc, type) => acc + this.getTaskTally(type) * dioramaFillConversion[type], 0);
  }

  public getOutstandingTally(taskType: LevelTaskType): number {
    const task = this.tasks.find(({ type }) => type === taskType);
    if (!task) return 0;
    return Math.max(task.goal - task.collected, 0);
  }

  public isActiveTask(taskType: LevelTaskType): boolean {
    return this.getOutstandingTally(taskType) > 0;
  }

  public getCompletedTaskTypes(): LevelTaskType[] {
    return this.tasks.filter(({ type }) => !this.isActiveTask(type)).map(({ type }) => type);
  }

  public getItemTaskType(item: object | object[]): LevelTaskType | undefined {
    const taskItem: object = Array.isArray(item) ? (item.at(0) as object) : item;

    return this.tasks.map(({ type }) => type).find((type) => levelTaskItemChecker[type](taskItem)) ?? undefined;
  }

  public debugCompleteAllTasks(): void {
    this.tasks.forEach((task) => {
      task.collected = task.goal;
    });
  }
}
